import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core'
import { CompanyManagementService } from '@app/features/company-management/service/company-management.service'
import { User } from '@core/model/user.model'
import { AuthService } from '@core/service/auth.service'
import { ActivatedRoute, Router } from '@angular/router'
import { LanguageService } from '@core/service/language.service'
import { MatDialog } from '@angular/material/dialog'
import { ScopeUiModalComponent } from '@shared/components/ui-components/scope-ui-modal/scope-ui-modal.component'
import { ModalConfig } from '@core/model/modal-config.model'
import {
  SNACKBAR_LENGTH_LONG,
  SNACKBAR_LENGTH_SHORT,
  SnackbarEventType,
  SnackbarService,
} from '@shared/utils/snackbar.service'
import { trackById } from '@shared/utils/utils'
import { FormControl } from '@angular/forms'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { RatecardStructureVersion } from '@app/features/scope-overview/model/ratecard-structure.model'
import { Company } from '@core/model/company.model'
import { UploadModalComponent } from '@shared/components/upload-modal/upload-modal.component'
import { ScopeTraffickingService } from '@app/features/scope-overview/service/scope-trafficking.service'
import { ImportRateCardModalComponent } from '@app/features/company-management/components/import-rate-card-modal/import-rate-card-modal.component'
import { UploadType } from '@app/features/company-management/models/upload-modal-config'
import { rateCardsColumnsBaseConfig } from '@app/features/company-management/store/models/rate-card-table-configs'
import { Preference, UserPreferences } from '@app/core/model/user-preferences.interface'
import { MenuOptions } from '@app/core/model/definitions/menu-options.interface'
import { UserService } from '@app/features/user-account/services/user.service'
import { UserPreferencesColumns } from '@app/core/model/enums/user-preferences-columns.enum'
import { ScopeUiTableSelectMode } from '@app/shared/components/ui-components/scope-ui-table/scope-ui-table.component'

const DEFAULT_PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

export enum RateCardViewMode {
  TILES = 'tiles',
  LIST = 'list',
}

@Component({
  selector: 'rate-cards',
  templateUrl: './rate-cards.component.html',
  styleUrls: ['./rate-cards.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RateCardsComponent implements OnInit {
  loggedInUser!: User
  loadingRateCards = true
  rateCards: RatecardVersion[]
  filteredRateCards: RatecardVersion[]
  archivedRateCards: RatecardVersion[]
  rateCardStructures: RatecardStructureVersion[]
  countCostPluses: number
  menuOptions: any[]
  downloadInProgress = {}
  searchText = ''
  showArchive: boolean
  page = 0
  totalCount = 0
  pageSize = DEFAULT_PAGE_SIZE
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS

  viewMode: RateCardViewMode = RateCardViewMode.TILES
  rateCardsColumns: Record<string, Preference> = rateCardsColumnsBaseConfig
  rateCardsPreferenceColumns: Preference[]
  rateCardMenuOptions: MenuOptions[]
  archivedRateCardMenuOptions: MenuOptions[]

  readonly RateCardViewMode = RateCardViewMode
  readonly ScopeUiTableSelectMode = ScopeUiTableSelectMode

  constructor(
    private authService: AuthService,
    private manageService: CompanyManagementService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    public route: ActivatedRoute,
    private lang: LanguageService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private traffickingService: ScopeTraffickingService,
    private userService: UserService
  ) {
    this.loggedInUser = this.authService.loggedInUser

    this.menuOptions = [
      {
        name: 'Import',
        icon: 'import_contacts',
        click: () => this.openImportRateCardModal(),
        isVisible: () => {
          return (
            this.hasValidThirdPartySystemSyncSettings() &&
            this.loggedInUser.hasPrivilege('COMPANY_TRAFFIC_SYSTEM__IMPORT')
          )
        },
      },
      {
        name: 'Sync',
        icon: 'sync',
        click: () => this.syncImported(),
        isVisible: () => {
          return (
            this.hasValidThirdPartySystemSyncSettings() &&
            this.loggedInUser.hasPrivilege('COMPANY_TRAFFIC_SYSTEM__IMPORT')
          )
        },
      },
      {
        name: `Upload Rate Card`,
        icon: 'import_export',
        click: () => this.upload(),
        isVisible: () => {
          return this.loggedInUser.hasPrivilege('COMPANY_TRAFFIC_SYSTEM__IMPORT')
        },
      },
      {
        name: `Export`,
        icon: 'file_download',
        click: () => this.export(),
        isVisible: () => {
          return this.rateCards?.length != 0
        },
      },
    ]
  }

  ngOnInit() {
    this.retrieveRateCards()
    this.retrieveArchivedRateCards()
    if (this.loggedInUser.hasPrivilege('RATECARD_STRUCTURE__VIEW')) {
      this.retrieveRateCardStructures()
    }
    if (this.loggedInUser.company.hasApplicationSetting('COST_PLUS')) {
      this.retrieveCostPlus()
    }

    this.initRateCardMenuOptions()
    this.initRateCardsPreferenceColumns()
    this.loadUserPreferences()
  }

  initRateCardMenuOptions() {
    this.rateCardMenuOptions = [
      {
        callback: (element) => this.showDeleteRateCardModal(element.entity),
        icon: () => 'archive',
        name: () => 'Archive',
        isHidden: ({ entity }) => entity.rateCardIdentity?.defaultCard,
        hasPrivilege: () => 'RATECARD__DELETE',
        hasPrivilegeEntity: ({ entity }) => entity,
      },
      {
        callback: (element) => this.showDuplicateRateCardModal(element.entity),
        icon: () => 'content_copy',
        name: () => 'Duplicate',
        hasPrivilege: () => 'RATECARD__CREATE',
      },
      {
        callback: (element) => this.exportSingleRateCard(element.entity),
        icon: () => 'file_download',
        name: () => 'Export',
      },
    ]

    this.archivedRateCardMenuOptions = [
      {
        callback: (element) => this.showUnarchiveRateCardModal(element.entity),
        icon: () => 'unarchive',
        name: () => 'Unarchive',
        isHidden: ({ entity }) => entity.rateCardIdentity?.defaultCard,
        hasPrivilege: () => 'RATECARD__DELETE',
        hasPrivilegeEntity: ({ entity }) => entity,
      },
    ]
  }

  initRateCardsPreferenceColumns() {
    this.rateCardsPreferenceColumns = Object.values(this.rateCardsColumns)
  }

  loadUserPreferences() {
    this.userService.getUserPreferences(UserPreferencesColumns.RATECARDS_VIEWMODE).subscribe({
      next: (preferences: any) => {
        if (preferences && preferences.viewMode) {
          this.viewMode = preferences.viewMode
        }
        this.cdr.detectChanges()
      },
      error: () => {
        this.viewMode = RateCardViewMode.TILES
      },
    })

    this.userService.getUserPreferences(UserPreferencesColumns.RATECARDS_USERCOLUMNS).subscribe({
      next: (preferences: UserPreferences) => {
        if (preferences) {
          Object.keys(this.rateCardsColumns).forEach((key) => {
            if (preferences[key]) {
              this.rateCardsColumns[key].selected = preferences[key].selected
            }
          })
          this.initRateCardsPreferenceColumns()
          this.cdr.detectChanges()
        }
      },
    })
  }

  onToggleViewMode(mode: RateCardViewMode) {
    this.viewMode = mode
    this.saveViewModePreference()
    this.cdr.detectChanges()
  }

  saveViewModePreference() {
    this.userService
      .setUserPreferences(UserPreferencesColumns.RATECARDS_VIEWMODE, { viewMode: this.viewMode } as any)
      .subscribe()
  }

  onTogglePreference(updatedPreference: Preference) {
    this.rateCardsColumns[updatedPreference.key].selected = updatedPreference.selected
    this.initRateCardsPreferenceColumns()
    this.saveColumnPreferences()
    this.cdr.detectChanges()
  }

  saveColumnPreferences() {
    this.userService.setUserPreferences(UserPreferencesColumns.RATECARDS_USERCOLUMNS, this.rateCardsColumns).subscribe()
  }

  onSelectRateCard(rateCard: RatecardVersion) {
    this.selectRateCard(rateCard.rateCardIdentity.id)
  }

  filterRateCards() {
    this.page = 0
    this.retrieveRateCards()
  }

  hasValidThirdPartySystemSyncSettings() {
    const company = this.loggedInUser.company
    const ts = company.trafficSystemSettings ? company.trafficSystemSettings.trafficSystem : null
    if (ts == null) {
      return false
    }
    if (ts.sourceType == 'SCOPE') {
      return true
    }
    const config = ts.externalTrafficSystemConfig
    return !config || (config.ratecard && config.ratecard.url)
  }

  retrieveRateCards() {
    this.loadingRateCards = true
    this.manageService.getCompanyRateCardsPaged(this.page, this.pageSize, this.searchText).subscribe({
      next: (result) => {
        this.loadingRateCards = false
        this.rateCards = result?.content || []
        this.filteredRateCards = result?.content || []
        this.totalCount = result?.totalElements || 0
        this.cdr.detectChanges()
      },
      error: () => {
        this.loadingRateCards = false
        this.snackbarService.showDefaultErrorSnackbar()
        this.cdr.detectChanges()
      },
    })
  }

  retrieveArchivedRateCards() {
    this.manageService.getAllRateCards(true, false).subscribe({
      next: (result) => {
        this.archivedRateCards = result
        this.cdr.detectChanges()
      },
    })
  }

  retrieveRateCardStructures() {
    this.manageService.getRateCardStructures().subscribe({
      next: (result: RatecardStructureVersion[]) => {
        this.rateCardStructures = result
          .filter((r) => !r.identity.archived)
          .sort((a, b) => a.name.localeCompare(b.name))
      },
    })
  }

  retrieveCostPlus() {
    this.manageService.getAllCostPluses().subscribe({
      next: (result) => {
        this.countCostPluses = result.length
      },
    })
  }

  selectRateCard(id) {
    this.router.navigate([`/manage/ratecard/${id}`], { relativeTo: this.route })
  }

  private executeRateCardExport(rateCard: RatecardVersion) {
    if (this.downloadInProgress[rateCard.id]) {
      this.snackbarService.showSnackbar('Download already in progress', SNACKBAR_LENGTH_LONG, SnackbarEventType.WARNING)
      return
    }

    this.manageService.requestExportOfRatecard(rateCard).subscribe({
      next: (result: any) => {
        if (!result.resolved) {
          this.downloadInProgress[rateCard.id] = true
          this.snackbarService.showSnackbar(
            'Export started and when complete file will automatically download',
            SNACKBAR_LENGTH_LONG,
            SnackbarEventType.INFO
          )
        }
      },
      error: () => {
        this.downloadInProgress[rateCard.id] = false
        this.snackbarService.showSnackbar('Failed to download export', SNACKBAR_LENGTH_LONG, SnackbarEventType.ERROR)
      },
    })
  }

  exportSingleRateCard(rateCard: RatecardVersion) {
    this.executeRateCardExport(rateCard)
  }

  showUnarchiveRateCardModal(rateCard: RatecardVersion) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        'Unarchive Rate Card',
        `Are you sure you want to unarchive rate card ${rateCard.name}`,
        'Unarchive',
        'Cancel',
        () => {
          dialog.close()
          this.manageService.unarchiveRateCard(rateCard.rateCardIdentity.id).subscribe({
            next: (result) => {
              this.archivedRateCards = this.archivedRateCards.filter(
                (rt) => rt.rateCardIdentity.id !== result.rateCardIdentity.id
              )
              this.filteredRateCards.push(result)
              this.cdr.detectChanges()
              this.snackbarService.showSnackbar(
                `${rateCard.name} successfully unarchived`,
                SNACKBAR_LENGTH_SHORT,
                SnackbarEventType.SUCCESS
              )
            },
            error: (error) => {
              switch (error.status) {
                case 409:
                  this.snackbarService.showErrorSnackbar(
                    'Could not unarchive the card. ' + error.error.message,
                    SNACKBAR_LENGTH_LONG
                  )
                  return
                default:
                  this.snackbarService.showDefaultErrorSnackbar()
              }
            },
          })
        },
        undefined,
        undefined,
        undefined,
        true
      ),
    })
  }

  showDeleteRateCardModal(rateCard: RatecardVersion) {
    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Archive Rate Card`,
        `Are you sure you want to archive rate card ${rateCard.name}?`,
        'Archive',
        undefined,
        () => {
          dialog.close()
          this.manageService.deleteRateCard(rateCard.rateCardIdentity.id).subscribe({
            next: () => {
              this.retrieveRateCards()
              this.retrieveArchivedRateCards()
              this.cdr.detectChanges()
              this.snackbarService.showSnackbar(
                `${rateCard.name} successfully archived`,
                SNACKBAR_LENGTH_SHORT,
                SnackbarEventType.SUCCESS
              )
            },
            error: () => this.snackbarService.showDefaultErrorSnackbar(),
          })
        },
        undefined,
        undefined,
        undefined,
        true
      ),
    })
  }

  showDuplicateRateCardModal(rateCard: RatecardVersion) {
    let name = null
    let ind = 1
    while (name == null) {
      const rcName = rateCard.name + ' (Duplicate ' + ind + ')'
      const found = this.rateCards.find((rc) => {
        return rc.name.toLowerCase() == rcName.toLowerCase()
      })
      if (!found) name = rcName
      ind++
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Duplicate Rate Card`,
        `Are you sure you want to duplicate rate card ${rateCard.name}?`,
        'Duplicate',
        undefined,
        (form: FormControl) => {
          dialog.close()
          this.manageService
            .duplicateRateCard(rateCard.id, {
              ratecardName: form.get('name')?.value,
              currency: form.get('currency')?.value,
            })
            .subscribe({
              next: () => {
                this.retrieveRateCards()
                this.cdr.detectChanges()
                this.snackbarService.showSnackbar(
                  `${rateCard.name} successfully duplicated`,
                  SNACKBAR_LENGTH_SHORT,
                  SnackbarEventType.SUCCESS
                )
              },
              error: (error) => {
                if (error.status == 409) {
                  form.get('name').setErrors({ conflict: true })
                } else {
                  this.snackbarService.showDefaultErrorSnackbar()
                }
              },
            })
        },
        undefined,
        [
          {
            name: 'name',
            control: new FormControl(name),
            type: 'text',
            required: true,
            label: `Rate card name`,
            maxLength: 100,
          },
          {
            name: 'currency',
            control: new FormControl(rateCard.currencyCode),
            type: 'select',
            required: true,
            label: `Currency`,
            options: this.manageService.currencies,
          },
        ],
        undefined,
        true,
        undefined,
        (form: FormControl) => !!form.get('name')?.value && form.get('currency')?.value
      ),
    })
  }

  showAddRateCardDialog() {
    const inputs: any[] = [
      {
        name: 'name',
        control: new FormControl(''),
        type: 'text',
        required: true,
        label: `Rate card name`,
        maxLength: 100,
      },
      {
        name: 'currency',
        control: new FormControl(null),
        type: 'select',
        required: true,
        label: `Currency`,
        options: this.manageService.currencies,
      },
    ]

    if (this.loggedInUser.company.hasApplicationSetting('COST_PLUS') && this.countCostPluses) {
      inputs.push({
        name: 'costPlus',
        control: new FormControl(false),
        type: 'toggle',
        label: `Cost Plus`,
        required: false,
      })
    }

    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Create Rate Card`,
        `Create a new rate card providing name and currency.`,
        `Start building Rate Card`,
        undefined,
        (form: FormControl) => {
          const name = form.get('name').value
          if (this.rateCards.find((r) => r.name === name)) {
            form.get('name').setErrors({ conflict: true })
            return
          }
          dialog.close()
          this.router.navigate([`/manage/ratecard`], {
            relativeTo: this.route,
            queryParams: {
              isDefault: false,
              currencyCode: form.get('currency').value,
              name: name,
              isCostPlus: form.get('costPlus')?.value,
            },
          })
        },
        undefined,
        inputs,
        false,
        true,
        undefined,
        (form: FormControl) => !!form.get('name')?.value && form.get('currency')?.value
      ),
    })
  }

  isParentCard(rateCard: RatecardVersion, company: Company) {
    return rateCard.isOwnedByParentOf(company)
  }

  openImportRateCardModal() {
    this.traffickingService.assertActiveSystemConfigured(this.loggedInUser.company)

    const dialog = this.dialog.open(ImportRateCardModalComponent, {
      data: {
        trafficSystem: this.loggedInUser.company.trafficSystemSettings.trafficSystem,
        confirmCallback: (data: any[], hasErrors: boolean) => {
          if (!hasErrors) {
            dialog.close()
            this.retrieveRateCards()
            this.cdr.detectChanges()
          }
        },
        cancelCallback: () => dialog.close(),
      },
    })
  }

  syncImported() {
    this.traffickingService.assertActiveSystemConfigured(this.loggedInUser.company)

    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Sync Rate cards`,
        'Syncing Options',
        `Sync`,
        undefined,
        (form: FormControl) => {
          this.manageService.syncTrafficImportsRateCard(form.get('appSettings').value).subscribe({
            next: () => {
              dialog.close()
              this.page = 0
              this.retrieveRateCards()
            },
            error: (error) => {
              this.traffickingService.validateServerError(
                error,
                () => dialog.close(),
                () => this.renderDownloadFileSync()
              )
            },
          })
        },
        undefined,
        [
          {
            name: 'appSettings',
            control: new FormControl(''),
            type: 'toggle',
            required: true,
            label: `Force Sync Rate card names`,
          },
        ],
        false,
        true,
        undefined
      ),
    })
  }

  renderDownloadFileSync() {
    this.manageService.renderTrafficSystemErrorOnSync('secondparty').subscribe({
      next: (response: any) => {
        const blob = new Blob([JSON.stringify(response.data)], { type: 'text/plain' })
        const contentDispositionHeader = response.headers('Content-Disposition')
        const result = contentDispositionHeader.split(';')[1].trim().split('=')[1].replace(/"/g, '').trim()
        const name = result.length > 0 ? result : 'fileData.txt'
        const downloadUrl = (window.URL || window.webkitURL).createObjectURL(blob)
        const a = $(`<a href='${downloadUrl}' download="${name}"></a>`)
        a[0].click()
      },
      error: (error) => {
        if (error.status == 404) {
          this.snackbarService.showSnackbar(
            'The cached data has expired.',
            SNACKBAR_LENGTH_LONG,
            SnackbarEventType.ERROR
          )
        }
      },
    })
  }

  upload() {
    const dialog = this.dialog.open(UploadModalComponent, {
      data: {
        title: 'Upload Rate Card',
        body: `Upload Rate Card using Excel file`,
        type: UploadType.RATECARD,
        verifyService: this.manageService,
        submitFn: (file: File) => this.manageService.import(file, 'ratecard'),
        successFn: () => {
          dialog.close()
          this.snackbarService.showSnackbar(
            `File was successfully uploaded. Please, refresh the page.`,
            SNACKBAR_LENGTH_LONG,
            SnackbarEventType.SUCCESS
          )
        },
        showBody: true,
        ratecards: this.rateCards,
      },
    })
  }

  private export() {
    const options = [{ id: null, name: 'All Rate Cards' }].concat(this.rateCards)

    const dialog = this.dialog.open(ScopeUiModalComponent, {
      data: new ModalConfig(
        `Download Rate Card(s)`,
        undefined,
        `Download`,
        undefined,
        (form: FormControl) => {
          dialog.close()
          const rateCard = form.get('rateCard').value
          this.executeRateCardExport(rateCard)
        },
        undefined,
        [
          {
            name: 'rateCard',
            control: new FormControl({ id: null, name: 'All Rate Cards' }),
            type: 'select',
            required: true,
            label: `Choose the rate card`,
            options: options,
          },
        ],
        false,
        true,
        undefined
      ),
    })
  }

  onChangePage(pageEvent: { pageIndex: number; pageSize: number }) {
    const { pageIndex, pageSize } = pageEvent
    this.page = pageIndex
    if (this.pageSize !== pageSize) {
      this.pageSize = pageSize
    }
    this.retrieveRateCards()
  }

  protected readonly trackById = trackById
}
